import * as React from "react";

import { Alert, Button, notify } from "Components/ui";
import { Check, Copy, Eye, EyeOff } from "Components/ui/icons";
import { EnvBlock } from "api/teleopdLink";
import { copyToClipboard } from "utils/clipboard";
import { formatDateTime } from "utils/format";
import DialogShell from "Routes/Billing/Components/DialogShell";

export type EnvBlockContext = "connect" | "reveal" | "rotate";

interface EnvBlockModalProps {
  envBlock: EnvBlock;
  context: EnvBlockContext;
  previousValidUntil?: string | null;
  onClose: () => void;
}

const VAR_HELP: Record<keyof EnvBlock["vars"], string> = {
  SAP_BASE_URL: "Where TeleOpd reaches SAP.",
  SAP_KEY_ID: "Identifies this client's key.",
  SAP_KEY_SECRET: "Signing secret. Treat it like a password.",
  SAP_LOCK: "shadow = only count what would be blocked; on = enforce read-only.",
};

const VAR_ORDER: Array<keyof EnvBlock["vars"]> = [
  "SAP_BASE_URL",
  "SAP_KEY_ID",
  "SAP_KEY_SECRET",
  "SAP_LOCK",
];

const INLINE_CODE = "rounded bg-surface-muted px-1 font-mono text-xs";

const TITLES: Record<EnvBlockContext, string> = {
  connect: "Add these keys to TeleOpd",
  reveal: "TeleOpd keys",
  rotate: "New TeleOpd keys",
};

/** Shows the TeleOpd env block once, with per-variable copy buttons and set-up steps. */
const EnvBlockModal: React.FC<EnvBlockModalProps> = ({
  envBlock,
  context,
  previousValidUntil,
  onClose,
}) => {
  const [copied, setCopied] = React.useState<string | null>(null);
  const [showSecret, setShowSecret] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const handle = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(handle);
  }, [copied]);

  const copy = async (id: string, text: string) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(id);
    } else {
      notify.error("Couldn't copy. Select the text and copy it manually.");
    }
  };

  return (
    <DialogShell
      title={TITLES[context]}
      subtitle={
        <>
          Key <span className="font-mono">{envBlock.keyId}</span> · version {envBlock.version}
        </>
      }
      size="lg"
      closeOnBackdrop={false}
      onClose={onClose}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => copy("all", envBlock.text)}
            leftIcon={copied === "all" ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          >
            {copied === "all" ? "Copied" : "Copy all"}
          </Button>
          <Button onClick={onClose}>I've saved these</Button>
        </>
      }
    >
      <div className="space-y-4">
        {context === "rotate" && (
          <Alert tone="warning" title="Update TeleOpd soon">
            The previous key keeps working
            {previousValidUntil
              ? ` until ${formatDateTime(previousValidUntil)}`
              : " for a few days"}
            , then TeleOpd stops syncing until it has these new values.
          </Alert>
        )}
        {!envBlock.sapBaseUrlConfigured && (
          <Alert tone="warning" title="SAP_BASE_URL is empty">
            SAP_PUBLIC_BASE_URL isn't set on the SAP server, so fill in SAP's public address (for
            example https://sap.example.com) by hand.
          </Alert>
        )}

        <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
          <li>
            Add these variables to the TeleOpd deployment's environment (.env, pm2 ecosystem or
            container settings).
          </li>
          <li>Restart TeleOpd.</li>
          <li>
            Come back here: the first sync shows up within about 5 minutes.{" "}
            <code className={INLINE_CODE}>SAP_LOCK</code> starts as{" "}
            <code className={INLINE_CODE}>shadow</code>, so TeleOpd only counts what it would block
            until you switch it to <code className={INLINE_CODE}>on</code>.
          </li>
        </ol>

        <ul className="divide-y divide-border rounded-card border border-border">
          {VAR_ORDER.map((name) => {
            const value = envBlock.vars[name];
            const secret = name === "SAP_KEY_SECRET";
            const display = secret && !showSecret ? "•".repeat(24) : value || "(empty)";
            return (
              <li key={name} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-xs font-semibold text-muted-foreground">{name}</p>
                  <p className="mt-0.5 break-all font-mono text-sm text-foreground">{display}</p>
                  <p className="mt-0.5 text-xs text-subtle-foreground">{VAR_HELP[name]}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {secret && (
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setShowSecret((current) => !current)}
                      aria-pressed={showSecret}
                      leftIcon={
                        showSecret ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />
                      }
                    >
                      {showSecret ? "Hide" : "Show"}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => copy(name, `${name}=${value}`)}
                    disabled={!value}
                    aria-label={copied === name ? `Copied ${name}` : `Copy ${name}`}
                    leftIcon={
                      copied === name ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />
                    }
                  >
                    {copied === name ? "Copied" : "Copy"}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-muted-foreground">
          SAP doesn't store the secret. Anyone with billing configure access can show these again
          with Reveal keys (recorded in the audit log).
        </p>
      </div>
    </DialogShell>
  );
};

export default EnvBlockModal;
